import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ExtraOptions, PreloadAllModules, RouterModule } from '@angular/router';
import { FuseModule } from '@fuse';
import { FuseConfigModule } from '@fuse/services/config';
import { CoreModule } from 'app/core/core.module';
import { appConfig } from 'app/core/config/app.config';
import { LayoutModule } from 'app/layout/layout.module';
import { AppComponent } from 'app/app.component';
import { appRoutes } from 'app/app.routing';
import { NgxPanZoomModule } from 'ngx-panzoom';
import { HttpClientModule } from '@angular/common/http';
import { TranslocoRootModule } from './transloco-root.module';
import { NgxSpinnerModule } from 'ngx-spinner';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';
import { MatDialogModule } from '@angular/material/dialog';
import { NgxCaptchaModule } from 'ngx-captcha';
import { TranslocoService } from '@ngneat/transloco';
import { NgxEchartsModule } from 'ngx-echarts';
import { QuillModule } from 'ngx-quill';

import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatPaginatorIntlCro } from './modules/p-dashboard/mat-paginator-intl';
import {
  NGX_MAT_DATE_FORMATS,
  NgxMatDateAdapter,
  NgxMatDateFormats,
  NgxMatDatetimePickerModule,
  NgxMatNativeDateModule,
  NgxMatTimepickerModule,
} from '@angular-material-components/datetime-picker';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { NGX_MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular-material-components/moment-adapter';
import { CustomNgxDatetimeAdapter } from './services/CustomNgxDatetimeAdapter';
import { environment } from '../environments/environment';
import { MyErrorHandler } from './my-error-handler';
import { KeycloakService } from './core/auth/keycloak.service';
import { getSaver, SAVER } from './shared/classes/saver.provider';
import { ToastrModule } from 'ngx-toastr';
import { NgxImageZoomModule } from 'ngx-image-zoom';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireMessagingModule } from '@angular/fire/compat/messaging';
import { NgxMaskModule } from 'ngx-mask';

const routerConfig: ExtraOptions = {
  scrollPositionRestoration: 'enabled',
  preloadingStrategy: PreloadAllModules,
  relativeLinkResolution: 'legacy',
};
const CUSTOM_DATE_FORMATS: NgxMatDateFormats = {
  parse: {
    dateInput: 'l, LTS',
  },
  display: {
    dateInput: 'YYYY-MM-DD HH:mm:ss',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export function initializeFirebaseConfig(): any | null {
  const ua = window.navigator.userAgent;
  if (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(ua)
  ) {
    return null;
  } else {
    return environment.firebaseConfig;
  }
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    NgxImageZoomModule,
    BrowserAnimationsModule,
    RouterModule.forRoot(appRoutes, routerConfig),
    AngularFireModule.initializeApp(initializeFirebaseConfig() || {}),
    AngularFireMessagingModule,
    QuillModule.forRoot({
      customOptions: [
        {
          import: 'formats/font',
          whitelist: ['mirza', 'roboto', 'aref', 'serif', 'sans-serif', 'monospace'],
        },
      ],
    }),
    NgxEchartsModule.forRoot({
      echarts: () => import('echarts'),
    }),

    // Fuse & Fuse Mock API
    FuseModule,
    FuseConfigModule.forRoot(appConfig),

    // Core
    CoreModule,

    // Layout
    LayoutModule,

    // 3rd party modules
    ToastrModule.forRoot({
      timeOut: 6000,
      positionClass: 'toast-top-left',
      preventDuplicates: true,
      progressBar: true,
      closeButton: true,
    }),

    NgxPanZoomModule,
    HttpClientModule,
    TranslocoRootModule,
    NgxSpinnerModule,
    NgxGoogleAnalyticsModule.forRoot(environment.googleAnalyticsId),
    NgxGoogleAnalyticsRouterModule,

    MatDialogModule,
    NgxCaptchaModule,

    // Date and time picker
    NgxMatDatetimePickerModule,
    NgxMatTimepickerModule,
    NgxMatNativeDateModule,
    NgxMaskModule.forRoot(),
  ],
  bootstrap: [AppComponent],

  providers: [
    // Provide custom MatPaginatorIntl
    {
      provide: MatPaginatorIntl,
      useClass: MatPaginatorIntlCro,
    },
    {
      provide: NgxMatDateAdapter,
      useClass: CustomNgxDatetimeAdapter,
      deps: [MAT_DATE_LOCALE, NGX_MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: (translate: TranslocoService) => translate.getActiveLang(),
      deps: [TranslocoService],
    },
    { provide: NGX_MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    { provide: SAVER, useFactory: getSaver },
    { provide: ErrorHandler, useClass: MyErrorHandler },
    {
      provide: APP_INITIALIZER,
      useFactory: (keycloakService: KeycloakService) => () => keycloakService.init(),
      deps: [KeycloakService],
      multi: true,
    },
  ],
})
export class AppModule {
  constructor() {}
}
